/**
 * Universal cart drawer auto-open.
 *
 * The theme's product-page form handler (theme.js onFormSubmit ~line 7626)
 * triggers `quick-cart:open` after a successful Add to Cart. But several
 * OTHER code paths that add items to the cart do NOT fire this event:
 *   - product-item quick-add on collection pages (~line 5737)
 *   - quantity increment (+) buttons on product item cards (~line 3455)
 *   - cross-sell "Add" buttons in the cart drawer itself (~line 9736 — but
 *     that's fine because the drawer is already open)
 *   - complementary products Add button on the PDP
 *   - any third-party code that calls /cart/add.js directly
 *
 * Result: users who add from anywhere except the main PDP form see no visual
 * feedback, because the drawer never opens automatically. This script closes
 * that gap by intercepting fetch() calls to /cart/add.js and /cart/change.js
 * and, on success, programmatically clicking the header cart trigger — which
 * goes through the theme's own delegate handler and properly opens the drawer
 * with focus trap + body scroll lock intact.
 *
 * Idempotent: if the drawer is already open (e.g. the PDP form path fired its
 * own quick-cart:open) this script does nothing.
 */
(function () {
  'use strict';

  var OPEN_DELAY_MS = 300;
  var path = window.location.pathname.replace(/\/+$/, '');
  var EXCLUDED_CART_OPEN_PATHS = [
  '/products/build-your-own-starter-kit',
  '/products/byoextension',
  '/pages/swipe-shades'   // 
];
var skipUniversalCartOpenOnThisPage =
  EXCLUDED_CART_OPEN_PATHS.indexOf(path) !== -1;
  // ============================================================================
  // Intercept native form submissions to /cart/add for the top-product-card
  // component (used on /collections/most-wanted-colours and similar "top N"
  // template pages). The component emits:
  //
  //   <form action="/cart/add" method="post" class="js-ajax-add-to-cart
  //                                                 top-product-card__form">
  //     <input type="hidden" name="id" value="...">
  //     <input type="hidden" name="quantity" value="1">
  //     <button type="submit">Add to Cart</button>
  //   </form>
  //
  // Nothing in the theme actually intercepts `.js-ajax-add-to-cart`, so the
  // form submits natively, Shopify 302s to /cart, and the user gets yanked
  // to the cart page instead of the drawer opening. We catch the submit at
  // the document level (capture phase so we win the race against anything
  // else) and route it through /cart/add.js instead. The fetch monkey-patch
  // below then picks up the /cart/add.js response and opens the drawer.
  // ============================================================================
  document.addEventListener(
    'submit',
    function (e) {
      if (skipUniversalCartOpenOnThisPage) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (
        !form.classList.contains('js-ajax-add-to-cart') &&
        !form.classList.contains('top-product-card__form')
      ) {
        return;
      }
      var action = (form.getAttribute('action') || '').split('?')[0];
      if (!/\/cart\/add\/?$/.test(action)) return;

      e.preventDefault();
      e.stopPropagation();

      var btn = form.querySelector('button[type="submit"]');
      if (btn) {
        if (btn.dataset.ucoLoading === '1') return;
        btn.dataset.ucoLoading = '1';
        btn.classList.add('loading');
        btn.disabled = true;
      }

      var formData = new FormData(form);
      var payload = {};
      formData.forEach(function (value, key) {
        payload[key] = value;
      });
      if (payload.id) payload.id = parseInt(payload.id, 10);
      if (payload.quantity) payload.quantity = parseInt(payload.quantity, 10) || 1;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed: ' + r.status);
          return r.json();
        })
        .then(function (item) {
          try {
            document.dispatchEvent(
              new CustomEvent('cart:item-added', { detail: { product: item } })
            );
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: {} }));
          } catch (err) { /* ignore */ }

          // Bump the visible "N in cart" counter if the button shows one
          if (btn) {
            var currentText = (btn.textContent || '').trim();
            var m = currentText.match(/Add More \((\d+) in cart\)/);
            if (m) {
              btn.textContent = 'Add More (' + (parseInt(m[1], 10) + 1) + ' in cart)';
            } else if (/^Add to Cart$/i.test(currentText)) {
              btn.textContent = 'Add More (1 in cart)';
            }
          }

          // The fetch monkey-patch (below) handles drawer opening.
        })
        .catch(function () {})
        .then(function () {
          if (btn) {
            btn.classList.remove('loading');
            btn.disabled = false;
            btn.dataset.ucoLoading = '0';
          }
        });
    },
    true // capture phase
  );

  // ============================================================================
  // Intercept anchor clicks to `/cart/add?id=...` and `/cart/add?id[]=...`.
  //
  // Several templates build anchor elements that link directly to the cart/add
  // endpoint (not the .js variant):
  //   - templates/product.nail-mail-offer-page.json — promotion grid tiles
  //   - templates/product.nicole-nail-mail.json — promotion grid tiles
  //   - templates/product.byosk.json — "Build Your Starter Kit" button with
  //                                    array id[] for multi-item add
  //
  // Clicking these normally navigates the browser to /cart/add?..., Shopify
  // adds the item(s), and 302s to /cart — yanking the customer out of their
  // current flow. We catch the click, parse the query string, and route
  // through /cart/add.js via fetch so the item adds + drawer opens instead.
  // ============================================================================
  document.addEventListener(
    'click',
    function (e) {
      if (skipUniversalCartOpenOnThisPage) return;
      // Only left-click with no modifiers (let ctrl/cmd-click still open a
      // new tab with the real /cart/add URL if the user insists)
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

      var anchor = e.target && e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      var href = anchor.getAttribute('href') || '';
      // Match /cart/add followed by ? or end, with query containing id= or id[]=
      if (!/\/cart\/add(\?|$)/.test(href)) return;
      if (!/[?&](id|id%5B%5D|id\[\])=/.test(href)) return;

      e.preventDefault();
      e.stopPropagation();

      // Parse the query string. Handles:
      //   ?id=123&quantity=1          — single item
      //   ?id[]=123&id[]=456          — multiple items (quantity 1 each)
      //   ?id%5B%5D=123&id%5B%5D=456  — URL-encoded brackets
      var queryStr = href.split('?')[1] || '';
      var params = new URLSearchParams(queryStr);
      var items = [];
      var ids = params.getAll('id');
      var arrayIds = params.getAll('id[]').concat(params.getAll('id%5B%5D'));
      var quantity = parseInt(params.get('quantity'), 10) || 1;

      if (arrayIds.length > 0) {
        // Multi-item array form
        for (var i = 0; i < arrayIds.length; i++) {
          items.push({ id: parseInt(arrayIds[i], 10), quantity: 1 });
        }
      } else if (ids.length > 0) {
        for (var j = 0; j < ids.length; j++) {
          items.push({ id: parseInt(ids[j], 10), quantity: quantity });
        }
      }

      if (items.length === 0) return;

      // /cart/add.js supports a batch `items` array — one round trip for N items.
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('batch add failed: ' + r.status);
          return r.json();
        })
        .then(function () {
          try {
            document.dispatchEvent(new CustomEvent('cart:item-added', { detail: {} }));
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: {} }));
          } catch (err) { /* ignore */ }
          // The fetch monkey-patch below catches /cart/add.js and opens + refreshes
          // the drawer via openDrawerIfClosed + refreshDrawerContents.
        })
        .catch(function () {
          // On failure, fall back to native navigation so the user isn't left
          // with a silent no-op. Browser will follow the original anchor href.
          window.location.href = href;
        });
    },
    true // capture phase so we win the race against other click handlers
  );

  var origFetch = window.fetch;
  if (!origFetch || origFetch.__bluesky_universal_cart_patched) return;

  window.fetch = function (url, opts) {
    var options = opts || {};
    var headers = options.headers || {};
    var urlStr = typeof url === 'string' ? url : (url && url.url) || '';

    var skipUniversalHandling = false;

    try {
      if (headers instanceof Headers) {
        skipUniversalHandling = headers.get('X-Skip-Universal-Cart-Open') === '1';
      } else {
        skipUniversalHandling =
          headers['X-Skip-Universal-Cart-Open'] === '1' ||
          headers['x-skip-universal-cart-open'] === '1';
      }
    } catch (e) {}

    if (skipUniversalCartOpenOnThisPage) {
      skipUniversalHandling = true;
    }

    var result = origFetch.apply(this, arguments);

    if (
      !skipUniversalHandling &&
      /\/cart\/(add|change|update|clear)\.js/.test(urlStr)
    ) {
      result.then(function (response) {
        if (response && (response.ok || response.status === 200)) {
          refreshQuickAddQuantities();

          if (/\/cart\/(add|change|update)\.js/.test(urlStr)) {
            setTimeout(refreshDrawerContents, OPEN_DELAY_MS);
            setTimeout(openDrawerIfClosed, OPEN_DELAY_MS);
          }
        }
      }).catch(function () {});
    }

    return result;
  };
  window.fetch.__bluesky_universal_cart_patched = true;

  // Re-fetch the quick-cart section from Shopify and update the drawer's
  // stateful parts (cart items, heading count, subtotal, cart-total data
  // attribute for the shipping bar) — but NOT the cross-sells container or
  // shipping bar wrapper. Uses Shopify's `?section_id=` rendering pattern.
  //
  // Why targeted updates instead of whole-drawer replacement:
  // The cross-sells container is hydrated client-side by dynamic-cross-sells.js
  // with dynamic per-product recommendations. If we replace the whole drawer,
  // we clobber the hydrated content and race against the re-hydration timer,
  // causing the drawer to flicker between dynamic and static fallback items.
  // By only updating the parts that CHANGE per-cart (line items, subtotal,
  // totals), the cross-sells container keeps its hydrated state and our
  // dynamic-cross-sells.js script re-runs cleanly on the cart:updated event.
  //
  // Node-level DOM operations only — no innerHTML of fetched content.
  function refreshDrawerContents() {
    var section = document.querySelector('.quick-cart[data-section-id]');
    if (!section) return;
    var sectionId = section.getAttribute('data-section-id');
    if (!sectionId) return;

    origFetch('/cart?section_id=' + encodeURIComponent(sectionId), {
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Replace each stateful piece individually, preserving the outer
        // structure and anything with client-side state (cross-sells,
        // shipping bar wrapper with hydrated data attrs).
        replaceStatefulElement(doc, section, '.quick-cart__items');
        replaceStatefulElement(doc, section, '.quick-cart__heading');
        replaceStatefulElement(doc, section, '.quick-cart__footer-subtotal');
        // The free-shipping-bar's data-cart-total attribute needs to be
        // updated so the two-tier shipping bar script re-renders against
        // the new total. Replace the inner of the bar only if needed, or
        // just update the attribute.
        updateShippingBarCartTotal(doc, section);

        // Dispatch a DOM event so downstream scripts know the drawer has
        // fresh data (for any code that needs to react).
        try {
          document.dispatchEvent(new CustomEvent('drawer:refreshed', {}));
        } catch (e) { /* ignore */ }
      })
      .catch(function () {});
  }

  function replaceStatefulElement(freshDoc, currentSection, selector) {
    var fresh = freshDoc.querySelector(selector);
    var current = currentSection.querySelector(selector);
    if (!fresh || !current) return;
    var clone = fresh.cloneNode(true);
    current.parentNode.replaceChild(clone, current);
  }

  function updateShippingBarCartTotal(freshDoc, currentSection) {
    var freshBar = freshDoc.querySelector('[data-free-shipping-bar], [data-two-tier-shipping-bar]');
    var currentBar = currentSection.querySelector('[data-free-shipping-bar], [data-two-tier-shipping-bar]');
    if (!freshBar || !currentBar) return;
    var freshTotal = freshBar.getAttribute('data-cart-total');
    if (freshTotal !== null) {
      currentBar.setAttribute('data-cart-total', freshTotal);
      // The two-tier-shipping-bar.js MutationObserver watches this attribute
      // and re-renders the message + progress bar when it changes.
    }
  }

  // Also refresh on initial DOM ready (in case the server-rendered attribute
  // is out of sync with the actual cart state — shouldn't happen, but cheap insurance).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(refreshQuickAddQuantities, 100); });
  } else {
    setTimeout(refreshQuickAddQuantities, 100);
  }

  // Also refresh when the cart:updated / cart:item-added custom events fire.
  // These are dispatched by the theme's own cart module after mutations.
  document.addEventListener('cart:updated', function () { setTimeout(refreshQuickAddQuantities, 200); });
  document.addEventListener('cart:item-added', function () { setTimeout(refreshQuickAddQuantities, 200); });

  function refreshQuickAddQuantities() {
    origFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var qtyByVariant = {};
        for (var i = 0; i < cart.items.length; i++) {
          qtyByVariant[String(cart.items[i].variant_id)] = cart.items[i].quantity;
        }
        var buttons = document.querySelectorAll('.product-item__quick-add[data-variant-id]');
        for (var j = 0; j < buttons.length; j++) {
          var variantId = buttons[j].getAttribute('data-variant-id');
          var liveQty = qtyByVariant[variantId] || 0;
          // Update the data attribute — the theme reads this synchronously
          // on each click via e.currentTarget.dataset.quantityInCart.
          buttons[j].setAttribute('data-quantity-in-cart', String(liveQty));
        }
      })
      .catch(function () {});
  }

  function openDrawerIfClosed() {
    // Admin toggle: Theme settings → Cart → "Open cart drawer on add to cart",
    // rendered into window.blueskyAutoOpenCartDrawer by theme.liquid. Only an
    // explicit false disables the auto-open; null/undefined (setting never
    // saved) keeps the historical behaviour. Everything else this script does
    // (drawer refresh, redirect fixes, quantity badges) stays active.
    if (window.blueskyAutoOpenCartDrawer === false) return;

    var drawer = document.querySelector('.quick-cart__wrapper');
    if (!drawer) return;
    // Already open — theme's own handler got there first. Don't double-trigger.
    if (drawer.classList.contains('active')) return;

    // Find the header cart trigger. The theme registers a click delegate on
    // this element that calls preventDefault + r$1("quick-cart:open"), which
    // opens the drawer through the theme's own code path (including focus
    // trap and body scroll lock setup). This is the same thing that happens
    // when a real user clicks the cart icon in the header.
    var cartTrigger = document.querySelector('[data-header-cart]');
    if (!cartTrigger) return;

    // Only click if it's actually visible (not hidden behind a mobile menu etc).
    if (cartTrigger.offsetParent === null) {
      // Fall back to any visible cart trigger
      var visibleTriggers = document.querySelectorAll('[data-header-cart]');
      for (var i = 0; i < visibleTriggers.length; i++) {
        if (visibleTriggers[i].offsetParent !== null) {
          visibleTriggers[i].click();
          return;
        }
      }
      return;
    }

    cartTrigger.click();
  }
})();
